import { uniq } from 'lodash';
import { useEffect } from 'react';
import { PartialSubmission } from 'types';
import { stringCompare } from 'utils/comparators';
import { HotjarEvents } from 'utils/hotjar-events';
import useSubmissionFilter from './useSubmissionFilter';
import { SubmissionBoxStorageKeys, SubmissionsViewStorageKeys, SubmissionViewMode } from './utils';

interface useRenewalBookFilterProps {
  submissions: PartialSubmission[];
  areSubmissionsLoaded: boolean;
  paginationReset: () => void;
  mode: SubmissionViewMode;
}

export function useRenewalBookFilter({
  submissions,
  areSubmissionsLoaded,
  paginationReset,
  mode,
}: useRenewalBookFilterProps) {
  const bookFilter = useSubmissionFilter({
    baseFiltersList: [],
    storageKey:
      mode === SubmissionViewMode.General ? SubmissionsViewStorageKeys.bookName : SubmissionBoxStorageKeys.bookName,
    hotjarEvent: HotjarEvents.SubmissionsRenewalBookFilter,
    paginationReset,
  });

  const setBookFilters = bookFilter.setFilters;

  // Update the filter options once we have the submissions from the server
  useEffect(() => {
    // We can't create booksList until data is loaded so just bail out
    if (!areSubmissionsLoaded || submissions.length === 0) {
      return;
    }

    const bookSetter = (booksList: string[]) => {
      setBookFilters((prev) =>
        booksList.map((book) => ({
          key: book,
          checked: prev.find((line) => line.key === book)?.checked ?? false,
          label: book,
        })),
      );
    };

    const booksList = uniq(submissions.filter(({ bookName }) => bookName).map(({ bookName }) => bookName!)).sort(
      stringCompare,
    );

    bookSetter(booksList);
  }, [areSubmissionsLoaded, submissions, setBookFilters]);

  return bookFilter;
}
